/**
 * Qué muestra el carrito cuando el pedido viene del QR de una mesa, y cuándo
 * deja avanzar al pago.
 *
 * Vive fuera del componente por la misma razón que `stepMachine`: son reglas con
 * casos, y el repo corre Jest en entorno node — sin esto no habría forma de
 * testearlas. Las dos que importan:
 *
 *  - El teléfono se ofrece en LOS DOS submodos. Antes solo existía en "para
 *    llevar", y el cliente que comía en la mesa y se levantaba quedaba
 *    incontactable: el caso que originó todo este trabajo.
 *  - El nombre es OBLIGATORIO. Si el cliente se movió y viene al mostrador, el
 *    mesero necesita a nombre de quién entregar; cantar "Mesa 1" es inútil justo
 *    cuando la persona ya no está ahí.
 *
 * Recibe el LABEL de la mesa, no el `TableContext` entero: es lo único que
 * necesita, y así estas reglas no quedan atadas a la forma de ese tipo (que la
 * Tarea 8 adelgaza). Interfaz angosta, test sin fixture.
 */

export type QrTableFields = {
  showQrPhone: boolean
  qrPhoneHelpKey: "tablePhoneHelp" | "takeoutPhoneHelp"
  /** Label de la mesa si corresponde dibujar el aviso, si no null. */
  tableNoticeLabel: string | null
  showName: boolean
}

export function qrTableFields(
  tableLabel: string | null | undefined,
  qrOrderType: "dine_in" | "takeout",
): QrTableFields {
  if (!tableLabel) {
    return {
      showQrPhone: false,
      qrPhoneHelpKey: "takeoutPhoneHelp",
      tableNoticeLabel: null,
      showName: false,
    }
  }
  const dineIn = qrOrderType === "dine_in"
  return {
    showQrPhone: true,
    qrPhoneHelpKey: dineIn ? "tablePhoneHelp" : "takeoutPhoneHelp",
    // Para llevar no va a ninguna mesa: el aviso sería falso.
    tableNoticeLabel: dineIn ? tableLabel : null,
    showName: true,
  }
}

/** ¿El nombre deja pasar al pago? Trivial hoy; existe para que sea testeable. */
export function nameGateSatisfied(showName: boolean, name: string): boolean {
  if (!showName) return true
  return name.trim().length > 0
}

/**
 * ¿El teléfono deja pasar al pago? Es OPCIONAL: en blanco pasa, y ese es el
 * caso normal. Solo bloquea un teléfono ESCRITO pero inválido.
 *
 * Por qué existe — es un bug de DINERO, no de forma. En este checkout el POST a
 * /api/online-orders ocurre DESPUÉS de cobrar la tarjeta. Un número a medias
 * ("305555") sale de `normalizePhone` (components/microsite/sheet/PaymentStep.tsx)
 * sin "+" — su rama final devuelve los dígitos tal cual "para que el servidor lo
 * rechace de forma determinista" — y el schema
 * (lib/online-orders/orderBodySchema.ts, `customer_phone: /^\+\d{10,15}$/`)
 * responde 400. El cliente queda COBRADO Y SIN ORDEN.
 *
 * Ese rechazo determinista está bien para el checkout online normal, porque ahí
 * CustomerStep bloquea "Continuar" con su `isPhoneValid` y el valor malo nunca
 * llega al cobro. El camino QR no tenía ese candado, y desde que el campo vive
 * en la pantalla por defecto de toda mesa la exposición es de todos los clientes.
 *
 * La regla es la MISMA que la de CustomerStep (allí está inline, no compartida):
 * entre 10 y 15 dígitos. No es un patrón nuevo — es exactamente el conjunto de
 * valores para los que `normalizePhone` produce algo que el regex del servidor
 * acepta, en TODAS sus ramas (10 → "+1"+10; 11 con 1 → "+"+11; ≥10 → "+"+n; con
 * "+" explícito → "+"+n). El test cruza las dos fuentes contra el schema real,
 * así que si alguna cambia, se cae ahí y no en producción.
 */
export function phoneGateSatisfied(showQrPhone: boolean, phone: string): boolean {
  if (!showQrPhone) return true
  const trimmed = phone.trim()
  if (trimmed.length === 0) return true
  const digits = trimmed.replace(/\D/g, "")
  // Sin dígitos, `normalizePhone` devuelve "" y el POST OMITE el campo: es
  // indistinguible de dejarlo en blanco, así que no hay nada que bloquear.
  if (digits.length === 0) return true
  return digits.length >= 10 && digits.length <= 15
}
